import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Pause, Play, Truck, Repeat, Undo2, Factory } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { IMAGES, subPrice } from "@/lib/api";
import { VideoEmbed } from "@/components/VideoEmbed";
import { Usd, UsdOnly, usePrice } from "@/components/Usd";
import { TikTokFeed } from "@/components/TikTokFeed";
import { Science, Range, TipsTeaser } from "@/components/HomeSections";
import { Advice, ShopStacks, HowItWorks, ScienceReels, LaunchAds, BlogTeaser, Story, MeetFinn } from "@/components/LongScroll";
import { useSeo } from "@/lib/seo";
import { SEO } from "@/data/seo";
import { MASCOT_ALT } from "@/data/seo";

const reducedMotion = () => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

const LOCAL_VIDEO = { "https://customer-assets-cm19k8pv.emergentagent.net/job_cod-and-chill/artifacts/b1cad806dce06fe7_video1_old_soul_fresh_omegas_9x16_1080_v6.mp4": "/media/hero-720.mp4" };

const afterLoad = (fn) => {
  const go = () => setTimeout(fn, 1200);
  if (document.readyState === "complete") go(); else window.addEventListener("load", go, { once: true });
};

const HeroVideo = ({ url }) => {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(!reducedMotion());
  const [ready, setReady] = useState(false);
  const local = LOCAL_VIDEO[url];
  useEffect(() => afterLoad(() => setReady(true)), []);
  useEffect(() => { if (ready && local) ref.current?.load(); }, [ready, local]);
  useEffect(() => {
    const v = ref.current;
    if (!v || !ready) return;
    if (playing) v.play?.().catch(() => setPlaying(false)); else v.pause?.();
  }, [playing, ready]);
  if (!/\.(mp4|webm|mov)(\?|$)/i.test(url)) return <div className="absolute inset-0"><VideoEmbed url={url} background testId="hero-video" /></div>;
  return (
    <>
      <video ref={ref} data-testid="hero-video" src={ready && !local ? url : undefined} poster={IMAGES.mascot} muted loop playsInline preload="metadata" autoPlay={playing && ready}
        aria-label="Grandpa Finn ad: old soul, fresh omegas" className="absolute inset-0 h-full w-full object-cover">
        {ready && local && <><source media="(max-width: 767px)" src={local.replace("720", "540")} type="video/mp4" /><source src={local} type="video/mp4" /></>}
      </video>
      <button type="button" data-testid="hero-video-toggle" onClick={() => setPlaying(!playing)} aria-label={playing ? "Pause video" : "Play video"}
        className="absolute bottom-4 right-4 grid h-11 w-11 place-items-center rounded-full bg-navy/80 text-sand backdrop-blur transition-colors hover:bg-navy">
        {playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
      </button>
    </>
  );
};

const Hero = () => {
  const { settings, byId } = useStore();
  const video = settings?.hero_video_url;
  const fish = byId["fish-oil"];
  const price = usePrice();
  return (
    <section data-testid="hero-section" className="bg-navy text-sand">
      <div className="container-gf py-16 md:py-24 grid gap-12 lg:grid-cols-[1.25fr_1fr] items-center">
        <div className="rise">
          <span className="label-chip">Omega-3 Fish Oil + Daily Multivitamin</span>
          <h1 data-testid="hero-title" className="mt-6 text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            Old-school habit.<br /><span className="text-foam italic">New-school you.</span>
          </h1>
          <p data-testid="hero-subtitle" className="mt-6 text-base md:text-lg text-sand/80 max-w-lg">
            Omega-3 fish oil from sardines and anchovies, with EPA & DHA, plus a daily multivitamin. Two easy habits from a laid-back surfer who's been doing this forever.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Link to="/shop/fish-oil" data-testid="hero-cta-button" className="btn-pop bg-sunny text-navy">Shop Fish Oil <ArrowRight className="h-5 w-5" /></Link>
            <Link to="/quiz" data-testid="hero-quiz-link" className="btn-ghost text-sand hover:bg-sand hover:text-navy">Take the quiz</Link>
          </div>
          {fish && (
            <p data-testid="hero-price-line" className="mt-6 text-sm text-sand/70">
              {price(fish.price_usd)} one-time · {price(subPrice(fish))} with Subscribe & save
            </p>
          )}
        </div>
        <div className="relative mx-auto w-full max-w-[340px] aspect-[9/16] overflow-hidden rounded-[2rem] bg-navy-dark shadow-[0_24px_60px_rgba(0,0,0,0.35)]">
          {video ? <HeroVideo url={video} /> : <img src={IMAGES.mascot} alt={MASCOT_ALT} fetchpriority="high" width="1000" height="1000" className="h-full w-full object-cover" />}
        </div>
      </div>
    </section>
  );
};

const TRUST = [
  { icon: Factory, text: "Fish oil manufactured in the USA" },
  { icon: Truck, text: "Ships to the US, Canada, Europe, Australia & NZ" },
  { icon: Repeat, text: "Subscribe & save. Cancel online anytime" },
  { icon: Undo2, text: "30-day refund window" },
];

const TrustStrip = () => (
  <section data-testid="trust-strip" className="bg-foam">
    <ul className="container-gf py-6 grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm font-medium">
      {TRUST.map(({ icon: Icon, text }, i) => (
        <li key={text} data-testid={`trust-item-${i}`} className="flex items-center gap-3"><Icon className="h-5 w-5 shrink-0" />{text}</li>
      ))}
    </ul>
  </section>
);


export default function Home() {
  useSeo(SEO.home);
  const { hash } = useLocation();
  useEffect(() => {
    if (hash === "#tips" || hash === "#waitlist") setTimeout(() => document.getElementById("tips")?.scrollIntoView({ behavior: "smooth" }), 80);
  }, [hash]);
  return (
    <UsdOnly.Provider value={true}>
    <div data-testid="home-page">
      <Hero />
      <TrustStrip />
      <Science />
      <Range />
      <TipsTeaser />
      <TikTokFeed />
      <Advice />
      <ShopStacks />
      <HowItWorks />
      <ScienceReels />
      <LaunchAds />
      <BlogTeaser />
      <Story />
      <MeetFinn />
    </div>
    </UsdOnly.Provider>
  );
}
